/**
 * 1.4.0 asset groups, stream G3: the Library's Grouped view (the table view only; the grid keeps its flat layout).
 *
 *  - GroupedToggle: next to the grid/table switch while the table is shown (f.grouped / f.setGrouped, remembered for
 *    the person).
 *  - GroupedResults: the tree in place of the table (LibraryPanes.tsx): group rows (chevron, aria-expanded, indent by
 *    depth; poster, title, type, number, match count, a link to /groups/:id, G2's AssignGroupForDeliveryButton) and
 *    asset rows (the normal table row with the member number in front), Load more per level, drop assets onto a group
 *    row to move them in. onVisibleItems(assets) tells the Library the assets in the visible order, so shift-click
 *    ranges, Cmd-A, the arrow keys and the selection bar work on what is on screen; ticking a group row selects its
 *    matching assets (asking above GROUP_SELECT_CONFIRM).
 *
 * How it is built: each loaded level (the root, and every expanded group) is a LevelLoader that renders nothing and
 * reports its rows; GroupedResults walks the levels from the root through the expanded groups into one flat list of
 * rows, which it renders as one table and from which it takes the visible order. The assets of a ticked group that are
 * not on screen follow the visible ones in what onVisibleItems reports, so the selection bar acts on them too.
 *
 * Searching (1.4.0 after the 1M cloud runs): with a search or filter the server scans the series, and walks the
 * ungrouped assets, a part at a time; a page that its budget (or a statement past its timeout) ended says `searching`
 * (fewer rows, maybe none). The next part of that level is asked for at once, and the foot of the tree says
 * "Searching more series…" ("…assets…" in the ungrouped part) meanwhile, never "Nothing matches" before the search is
 * done. Stop ends it (the part on its way is cancelled; "Search more" goes on), and leaving the view asks for no more
 * parts (the hook's query is cancelled with no one watching it). A count not found in time shows as "…".
 *
 * Motion (1.4.0, as the flat list's 1.2.6 fade-in): the root's first rows fade in (useEntryOrder), and so do a level's
 * first rows each time it is opened (useLevelEntry), staggered from its first child. Load more, later parts, refetches
 * and new searches don't animate; collapsing doesn't either, and reduced motion turns it off (styles.css, row-enter).
 *
 * Data: api/hooks/grouped.ts (useGroupedLevel, fetchGroupSelection). G3's file.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronRight, Layers, ListTree, Lock, RotateCcw, Square } from 'lucide-react';
import { GROUP_SELECT_CONFIRM, type AssetGroupDTO, type AssetSummary, type GroupedPageDTO, type GroupedParent, type GroupedRow, type GroupTypeTakenDetails } from '@prism/contracts';
import { ApiError, errorMessage } from '../../../api/client';
import { useMe } from '../../../api/hooks';
import { fetchGroupSelection, groupedParams, useGroupedLevel } from '../../../api/hooks/grouped';
import { queryKeys } from '../../../api/queryKeys';
import { useAddGroupMembers } from '../../../api/hooks/groups';
import type { LibraryFilters } from '../../../api/hooks/library';
import { markIfComplete, markLoaded } from '../../../components/SpriteThumb';
import { QcBadge } from '../../../components/QualityControl';
import { RightsMark } from '../../../components/RightsMark';
import { MasterBadge } from '../../../components/MasterBadge';
import { toast } from '../../../components/Toasts';
import type { useLibraryColumns } from '../../LibraryColumns';
import { ENTRY_CLEAR_MS, entryOrderOf, useEntryOrder, useLoadMore } from '../LibraryItems';
import { LibrarySelectionBar } from '../LibrarySelectionBar';
import type { LibraryFilterState } from '../useLibraryFilters';
import type { LibrarySelection, SelectGesture } from '../useLibrarySelection';
import { AssignGroupForDeliveryButton } from '../../groups/AssignGroupForDelivery';

/** Set to true when the Grouped view is built: the toggle shows and the Library uses GroupedResults. */
export const GROUPED_READY = true;

/** The drag of Library assets onto a group row (their ids, JSON). */
export const ASSETS_DRAG_TYPE = 'application/x-prism-assets';
/** Pixels of indent per level, as the Jobs tree. */
const INDENT = 22;

export function GroupedToggle({ f }: { f: LibraryFilterState }) {
  if (!GROUPED_READY) return null;
  return (
    <button type="button" className={`btn ${f.grouped ? '' : 'ghost'} sm grouped-toggle`} aria-pressed={f.grouped} onClick={() => f.setGrouped(!f.grouped)}
      data-testid="grouped-toggle" title="Show series and seasons as a tree (the table only)">
      <ListTree size={14} /> Grouped
    </button>
  );
}

export interface GroupedResultsProps {
  f: LibraryFilterState;
  cols: ReturnType<typeof useLibraryColumns>;
  focusedId: string | null;
  selection: LibrarySelection;
  onSelect: (id: string, gesture: SelectGesture) => void;
  onFocusItem: (id: string) => void;
  onCheck: (id: string, gesture: SelectGesture) => void;
  onOpen: (id: string) => void;
  /** The asset rows on screen, in their visible order (expanded groups included). */
  onVisibleItems: (items: AssetSummary[]) => void;
}

/** What a LevelLoader reports of its level. */
interface LevelState {
  rows: GroupedRow[];
  pending: boolean;
  error: string | null;
  hasNext: boolean;
  fetchingNext: boolean;
  /** The last page stopped on the server's budget (or a statement's timeout): the next part follows at once. */
  searching: boolean;
  /** Pages loaded (a part that found nothing still counts: the next one is asked for after it). */
  parts?: number;
  /** The last page's phase (the root: groups, then the ungrouped assets). */
  phase?: GroupedPageDTO['phase'];
}

/** One line of the flattened tree. */
type Line =
  | { kind: 'group'; key: string; depth: number; level: GroupedParent; row: Extract<GroupedRow, { kind: 'group' }>; expanded: boolean }
  | { kind: 'asset'; key: string; depth: number; level: GroupedParent; row: Extract<GroupedRow, { kind: 'asset' }>; memberLabel: string | null }
  | { kind: 'more'; key: string; depth: number; parent: GroupedParent; fetching: boolean; searching?: boolean }
  | { kind: 'loading'; key: string; depth: number }
  | { kind: 'error'; key: string; depth: number; parent: GroupedParent; message: string }
  | { kind: 'empty'; key: string; depth: number };

/**
 * The visible lines: the root's rows, and below each expanded group its level's rows (when loaded), with a Load more
 * per level. `loaders`: the levels that are on screen (the root and every expanded group that is shown).
 */
export function flattenLevels(levels: Record<string, LevelState | undefined>, open: ReadonlySet<string>): { lines: Line[]; loaders: GroupedParent[] } {
  const lines: Line[] = [];
  const loaders: GroupedParent[] = [];
  const walk = (parent: GroupedParent, depth: number, memberLabel: string | null) => {
    loaders.push(parent);
    const level = levels[parent];
    if (!level || (level.pending && !level.rows.length)) {
      lines.push({ kind: 'loading', key: `${parent}:loading`, depth });
      return;
    }
    if (level.error && !level.rows.length) {
      lines.push({ kind: 'error', key: `${parent}:error`, depth, parent, message: level.error });
      return;
    }
    if (!level.rows.length && parent !== 'root' && !level.searching) lines.push({ kind: 'empty', key: `${parent}:empty`, depth });
    for (const row of level.rows) {
      if (row.kind === 'group') {
        const expanded = open.has(row.group.id);
        lines.push({ kind: 'group', key: `g:${row.group.id}`, depth, level: parent, row, expanded });
        if (expanded) walk(`group:${row.group.id}`, depth + 1, row.group.type.memberLabel);
      } else {
        lines.push({ kind: 'asset', key: `a:${parent}:${row.asset.id}`, depth, level: parent, row, memberLabel });
      }
    }
    if (level.hasNext && parent !== 'root') {
      lines.push({ kind: 'more', key: `${parent}:more`, depth, parent, fetching: level.fetchingNext, ...(level.searching ? { searching: true } : {}) });
    }
  };
  walk('root', 0, null);
  return { lines, loaders };
}

/** Each level's group and asset rows (their line keys), in order. */
export function rowKeysByLevel(lines: readonly Line[]): Map<GroupedParent, string[]> {
  const out = new Map<GroupedParent, string[]>();
  for (const l of lines) {
    if (l.kind !== 'group' && l.kind !== 'asset') continue;
    const keys = out.get(l.level);
    if (keys) keys.push(l.key); else out.set(l.level, [l.key]);
  }
  return out;
}

/**
 * The rows that fade in, with their place in the stagger (by line key): the root's first rows (useEntryOrder, as the
 * flat list), and an opened level's first rows, taken when that level first shows rows and dropped after the stagger
 * has played. A level that is collapsed (no longer on screen) is forgotten, so opening it again animates again; Load
 * more, later parts and refetches of a level already taken don't animate, nor does a level kept open across a search.
 */
function useLevelEntry(lines: readonly Line[], loaders: readonly GroupedParent[]): (key: string) => number | undefined {
  const byLevel = rowKeysByLevel(lines);
  const root = useEntryOrder((byLevel.get('root') ?? []).map((id) => ({ id })));
  const orders = useRef(new Map<GroupedParent, ReadonlyMap<string, number>>());
  const shown = new Set(loaders);
  for (const p of [...orders.current.keys()]) if (!shown.has(p)) orders.current.delete(p);
  for (const [p, keys] of byLevel) if (p !== 'root' && !orders.current.has(p)) orders.current.set(p, entryOrderOf(keys));
  const taken = [...orders.current.keys()].join(',');
  const timers = useRef(new Map<ReadonlyMap<string, number>, ReturnType<typeof setTimeout>>());
  useEffect(() => {
    for (const [p, order] of orders.current) {
      if (!order.size || timers.current.has(order)) continue;
      timers.current.set(order, setTimeout(() => {
        timers.current.delete(order);
        if (orders.current.get(p) === order) orders.current.set(p, new Map());
      }, ENTRY_CLEAR_MS));
    }
  }, [taken]);
  useEffect(() => () => { for (const t of timers.current.values()) clearTimeout(t); }, []);
  return (key) => root.get(key) ?? findIn(orders.current, key);
}
const findIn = (orders: ReadonlyMap<string, ReadonlyMap<string, number>>, key: string) => {
  for (const o of orders.values()) { const i = o.get(key); if (i !== undefined) return i; }
  return undefined;
};
/** The fade-in class and step of a row (none when it is not one of those). */
const enterProps = (i: number | undefined) => (i === undefined ? {} : { cls: ' row-enter', style: { '--i': i } as React.CSSProperties });

/** Loads one level and reports it; renders nothing. */
function LevelLoader({ filters, parent, onState, fetchers }: {
  filters: LibraryFilters; parent: GroupedParent;
  onState: (parent: GroupedParent, s: LevelState) => void;
  fetchers: React.MutableRefObject<Map<string, () => void>>;
}) {
  const q = useGroupedLevel(filters, parent);
  const { data, isPending, error, hasNextPage, isFetchingNextPage, fetchNextPage } = q;
  fetchers.current.set(parent, () => { if (hasNextPage && !isFetchingNextPage) void fetchNextPage(); });
  useEffect(() => {
    onState(parent, {
      rows: data?.pages.flatMap((p) => p.rows) ?? [],
      pending: isPending, error: error ? errorMessage(error) : null, hasNext: hasNextPage, fetchingNext: isFetchingNextPage,
      searching: Boolean(data?.pages[data.pages.length - 1]?.searching) && hasNextPage,
      parts: data?.pages.length ?? 0, phase: data?.pages[data.pages.length - 1]?.phase,
    });
  }, [parent, data, isPending, error, hasNextPage, isFetchingNextPage, onState]);
  return null;
}

const gestureOf = (e: React.MouseEvent): SelectGesture => ({ metaKey: e.metaKey, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey });
const noShiftTextSelect = (e: React.MouseEvent) => { if (e.shiftKey) e.preventDefault(); };
const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

export function GroupedResults({ f, cols, focusedId, selection, onSelect, onFocusItem, onCheck, onOpen, onVisibleItems }: GroupedResultsProps) {
  const me = useMe().data;
  const canManage = Boolean(me?.permissions.includes('group.manage'));
  const filters: LibraryFilters = useMemo(
    () => ({ ...f.activeFilters, q: f.q, sort: f.sort, advanced: f.advancedQuery, group: f.group }),
    [f.activeFilters, f.q, f.sort, f.advancedQuery, f.group],
  );
  const filterKey = groupedParams(filters);
  const filtered = f.hasFilters;
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  const [levels, setLevels] = useState<Record<string, LevelState | undefined>>({});
  const fetchers = useRef(new Map<string, () => void>());
  const onState = useCallback((parent: GroupedParent, s: LevelState) => setLevels((prev) => ({ ...prev, [parent]: s })), []);
  // A ticked group's matching assets (fetched when ticked), by group id.
  const [ticked, setTicked] = useState<Record<string, AssetSummary[]>>({});
  useEffect(() => { setTicked({}); }, [filterKey]);

  const { lines, loaders } = useMemo(() => flattenLevels(levels, open), [levels, open]);
  const enterOf = useLevelEntry(lines, loaders);
  const visible = useMemo(() => lines.flatMap((l) => (l.kind === 'asset' ? [l.row.asset] : [])), [lines]);
  // The visible assets (each once, in order), then the ticked groups' assets that are not on screen.
  const reported = useMemo(() => {
    const seen = new Set<string>();
    const out: AssetSummary[] = [];
    for (const a of [...visible, ...Object.values(ticked).flat()]) if (!seen.has(a.id)) { seen.add(a.id); out.push(a); }
    return out;
  }, [visible, ticked]);
  const reportedKey = reported.map((a) => `${a.id}:${a.updatedAt}`).join(',');
  const latestReported = useRef(reported);
  latestReported.current = reported;
  useEffect(() => { onVisibleItems(latestReported.current); }, [reportedKey, onVisibleItems]);

  const toggleOpen = (id: string) => setOpen((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const root = levels.root;
  // A level whose last part stopped on the server's budget: ask for its next part at once (not only when the foot
  // scrolls into view), until Stop (for these filters). Each part loaded changes `due`, so the next one follows.
  const queryClient = useQueryClient();
  const [stoppedFor, setStoppedFor] = useState<string | null>(null);
  const stopped = stoppedFor === filterKey;
  const due = loaders.filter((p) => { const s = levels[p]; return s?.searching && !s.error && !s.fetchingNext; }).map((p) => `${p}:${levels[p]!.parts ?? 0}`).join(',');
  useEffect(() => {
    if (stopped || !due) return;
    for (const entry of due.split(',')) fetchers.current.get(entry.slice(0, entry.lastIndexOf(':')))?.();
  }, [due, stopped]);
  const anySearching = loaders.some((p) => levels[p]?.searching && !levels[p]!.error);
  const stopSearching = () => {
    setStoppedFor(filterKey);
    // The part on its way is cancelled (the request is aborted; the server stops its statement).
    void queryClient.cancelQueries({ queryKey: [...queryKeys.groupedAll(), filterKey] });
  };
  const rootSearching = Boolean(root?.searching && !root.error);
  const rootMore = useLoadMore({
    // Stopped: no part is asked for by scrolling either, until Search more.
    hasNextPage: Boolean(root?.hasNext) && !stopped, isFetchingNextPage: Boolean(root?.fetchingNext),
    fetchNextPage: async () => fetchers.current.get('root')?.(),
  });

  // ---- ticking a group row
  const tickState = (g: AssetGroupDTO): { checked: boolean; mixed: boolean } => {
    const list = ticked[g.id];
    if (!list?.length) return { checked: false, mixed: false };
    const n = list.filter((a) => selection.has(a.id)).length;
    return { checked: n === list.length, mixed: n > 0 && n < list.length };
  };
  const tickGroup = async (row: Extract<GroupedRow, { kind: 'group' }>) => {
    const g = row.group;
    const st = tickState(g);
    if (st.checked) {
      const drop = new Set(ticked[g.id]!.map((a) => a.id));
      selection.selectAll(selection.ids.filter((id) => !drop.has(id)));
      return;
    }
    if (row.matchCount === null ? !window.confirm(`Select the matching assets of ${g.title}?`)
      : row.matchCount > GROUP_SELECT_CONFIRM
        && !window.confirm(`Select the ${row.matchCountExact ? '' : 'first '}${plural(row.matchCount, 'matching asset')} of ${g.title}?`)) return;
    try {
      const res = await fetchGroupSelection(filters, g.id);
      if (res.incomplete) {
        toast(`The matching assets of ${g.title} could not be found in time. Try again in a moment.`, 'error');
        return;
      }
      setTicked((t) => ({ ...t, [g.id]: res.items }));
      const have = new Set(selection.ids);
      // The ticked assets join the reported list first (the effect above), then the selection takes them.
      latestReported.current = [...latestReported.current, ...res.items.filter((a) => !latestReported.current.some((x) => x.id === a.id))];
      onVisibleItems(latestReported.current);
      selection.selectAll([...selection.ids, ...res.items.map((a) => a.id).filter((id) => !have.has(id))]);
      if (res.truncated) toast(`The first ${plural(res.items.length, 'asset')} of ${g.title} are selected`);
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };

  // ---- dropping assets onto a group row (G1's members API; the same-type move asks first)
  const addMembers = useAddGroupMembers();
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const moveInto = async (g: AssetGroupDTO, assetIds: string[]) => {
    const n = assetIds.length;
    try {
      let r;
      try {
        r = await addMembers.mutateAsync({ id: g.id, assetIds });
      } catch (e) {
        if (!(e instanceof ApiError) || e.code !== 'GROUP_TYPE_TAKEN') throw e;
        const taken = ((e.details as GroupTypeTakenDetails | undefined)?.assets ?? []).filter((x) => x.groupId !== g.id);
        const from = [...new Set(taken.map((x) => x.title))];
        const kind = g.type.label.toLowerCase();
        const question = taken.length
          ? `${plural(taken.length, 'asset')} ${taken.length === 1 ? 'is' : 'are'} already in another ${kind}${from.length ? ` (${from.slice(0, 3).join(', ')}${from.length > 3 ? '…' : ''})` : ''}. An asset is in one ${kind} at a time: move ${taken.length === 1 ? 'it' : 'them'} into ${g.title}?`
          : `Some of these are already in another ${kind}. Move them into ${g.title}?`;
        if (!window.confirm(question)) return;
        r = await addMembers.mutateAsync({ id: g.id, assetIds, move: true });
      }
      const done = (r?.added.length ?? 0) + (r?.moved.length ?? 0);
      const refused = r?.refused.length ?? 0;
      toast(`${done ? `${plural(done, 'asset')} in ${g.title}` : `Nothing added to ${g.title}`}${refused ? ` (${refused} refused: ${r!.refused[0]!.message})` : ''}`, refused && !done ? 'error' : undefined);
    } catch (e) {
      toast(errorMessage(e) || `Could not add ${plural(n, 'asset')} to ${g.title}`, 'error');
    }
  };
  const dragIds = (id: string) => (selection.has(id) && selection.count > 1 ? [...selection.ids] : [id]);

  const isSelected = (id: string) => (selection.count ? selection.has(id) : id === focusedId);
  const columns = cols.shown;
  const span = 1 + columns.length;
  const chosen = selection.selectedOf(reported);
  const visibleChecked = visible.filter((a) => selection.has(a.id)).length;

  if (root && !root.pending && !root.error && !root.rows.length && !root.searching && !(stopped && root.hasNext)) {
    return (
      <>
        {loaders.map((p) => <LevelLoader key={`${filterKey}|${p}`} filters={filters} parent={p} onState={onState} fetchers={fetchers} />)}
        <div className="empty grouped-empty" data-testid="grouped-empty"><p className="muted">{filtered ? 'Nothing matches in any group.' : 'No assets yet.'}</p></div>
      </>
    );
  }

  return (
    <div className="grouped-view" data-testid="grouped-view">
      {loaders.map((p) => <LevelLoader key={`${filterKey}|${p}`} filters={filters} parent={p} onState={onState} fetchers={fetchers} />)}
      {/* The selection bar over what the tree shows (LibraryPanes leaves its own out in the Grouped view). */}
      {chosen.length >= 2 && <div className="grouped-selection"><LibrarySelectionBar selected={chosen} onClear={selection.clear} /></div>}
      <table className="table asset-table grouped-tree" role="treegrid" aria-label="Assets by group" aria-multiselectable="true" data-testid="grouped-tree">
        <thead>
          <tr>
            <th className="select-col">
              <input type="checkbox" className="select-check" aria-label="Select every asset shown" data-testid="grouped-check-all"
                checked={visible.length > 0 && visibleChecked === visible.length} ref={(el) => { if (el) el.indeterminate = visibleChecked > 0 && visibleChecked < visible.length; }}
                onChange={(e) => (e.target.checked ? selection.selectAll([...new Set([...selection.ids, ...visible.map((a) => a.id)])]) : selection.clear())} />
            </th>
            <th style={{ width: 88 }} aria-label="Preview" />
            <th>Title</th>
            {columns.map((c) => <th key={c.id}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => {
            const pad = { paddingLeft: 8 + l.depth * INDENT };
            if (l.kind === 'loading') return <tr key={l.key} className="grouped-note"><td /><td /><td colSpan={span} className="faint" style={pad}>Loading…</td></tr>;
            if (l.kind === 'empty') return <tr key={l.key} className="grouped-note"><td /><td /><td colSpan={span} className="faint" style={pad}>Nothing here matches.</td></tr>;
            if (l.kind === 'error') {
              return (
                <tr key={l.key} className="grouped-note"><td /><td /><td colSpan={span} style={pad}>
                  <span className="grouped-error"><AlertTriangle size={13} /> {l.message}</span>{' '}
                  <button className="btn ghost sm" onClick={() => fetchers.current.get(l.parent)?.()}><RotateCcw size={13} /> Try again</button>
                </td></tr>
              );
            }
            if (l.kind === 'more') {
              return (
                <tr key={l.key} className="grouped-note"><td /><td /><td colSpan={span} style={pad}>
                  {l.searching && !stopped ? <span className="faint" data-testid="grouped-level-searching">Searching…</span>
                    : <button className="btn ghost sm" disabled={l.fetching} onClick={() => fetchers.current.get(l.parent)?.()} data-testid="grouped-load-more">
                      {l.fetching ? 'Loading more…' : 'Load more'}
                    </button>}
                </td></tr>
              );
            }
            if (l.kind === 'group') {
              const { group: g, matchCount, matchCountExact } = l.row;
              const tick = tickState(g);
              // The group's own number (the season number), unless its title says it already ("Season 1").
              const showNumber = g.number !== null && !new RegExp(`\\b${g.number}\\b`).test(g.title);
              const enter = enterProps(enterOf(l.key));
              return (
                <tr key={l.key} className={`grouped-group${l.depth ? ' grouped-child' : ''}${dropTarget === g.id ? ' grouped-drop' : ''}${enter.cls ?? ''}`} style={enter.style}
                  data-testid="grouped-group" data-group-id={g.id} data-depth={l.depth} aria-level={l.depth + 1} aria-expanded={l.expanded}
                  onDragOver={canManage ? (e) => {
                    if (!e.dataTransfer.types.includes(ASSETS_DRAG_TYPE)) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (dropTarget !== g.id) setDropTarget(g.id);
                  } : undefined}
                  onDragLeave={canManage ? (e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropTarget(null); } : undefined}
                  onDrop={canManage ? (e) => {
                    setDropTarget(null);
                    const raw = e.dataTransfer.getData(ASSETS_DRAG_TYPE);
                    if (!raw) return;
                    e.preventDefault();
                    try {
                      const ids = JSON.parse(raw) as string[];
                      if (Array.isArray(ids) && ids.length) void moveInto(g, ids);
                    } catch { /* not ours */ }
                  } : undefined}>
                  <td className="select-col">
                    <input type="checkbox" className="select-check" checked={tick.checked} aria-label={`Select the matching assets of ${g.title}`} data-testid="grouped-group-check" tabIndex={-1}
                      ref={(el) => { if (el) el.indeterminate = tick.mixed; }} onChange={() => {}} onClick={(e) => { e.stopPropagation(); void tickGroup(l.row); }} />
                  </td>
                  <td className="thumb-col">
                    {g.posterUrl ? <img key={g.posterUrl} className="mini-thumb" src={g.posterUrl} alt="" loading="lazy" ref={markIfComplete} onLoad={markLoaded} onError={markLoaded} />
                      : <div className="mini-thumb grouped-poster-none" aria-hidden="true"><Layers size={16} /></div>}
                  </td>
                  <td colSpan={span}>
                    <div className="grouped-title" style={pad}>
                      <button type="button" className="job-toggle grouped-expand" aria-expanded={l.expanded} aria-label={`${l.expanded ? 'Collapse' : 'Expand'} ${g.type.label} ${g.title}`}
                        onClick={() => toggleOpen(g.id)} data-testid="grouped-expand">
                        {l.expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                      <span className="pill grouped-type">{g.type.label}</span>
                      <Link to={`/groups/${g.id}`} className="grouped-name" data-testid="grouped-group-link" title={`Open ${g.title}`}>{g.title}</Link>
                      {showNumber && <span className="grouped-number" title={`${g.type.numberLabel ?? 'Number'} ${g.number}`}>{g.number}</span>}
                      <span className="faint grouped-count" data-testid="grouped-count" title={matchCount === null ? 'Not counted in time' : undefined}>
                        {matchCount === null ? `… ${filtered ? 'matches' : 'assets'}`
                          : `${matchCountExact ? '' : 'over '}${plural(matchCount, filtered ? 'match' : 'asset', filtered ? 'matches' : 'assets')}`}
                      </span>
                      <span className="grouped-actions"><AssignGroupForDeliveryButton groupId={g.id} title={g.title} compact /></span>
                    </div>
                  </td>
                </tr>
              );
            }
            const a = l.row.asset;
            const n = l.row.memberNumber;
            const enter = enterProps(enterOf(l.key));
            return (
              <tr key={l.key} className={`grouped-asset${l.depth ? ' grouped-child' : ''}${enter.cls ?? ''}`} style={enter.style} aria-selected={isSelected(a.id)} aria-level={l.depth + 1}
                tabIndex={a.id === focusedId ? 0 : -1} data-asset-id={a.id} data-testid="asset-row" data-depth={l.depth}
                draggable onDragStart={(e) => {
                  const ids = dragIds(a.id);
                  e.dataTransfer.setData(ASSETS_DRAG_TYPE, JSON.stringify(ids));
                  e.dataTransfer.setData('text/plain', ids.length > 1 ? `${ids.length} assets` : a.title);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onMouseDown={noShiftTextSelect} onClick={(e) => onSelect(a.id, gestureOf(e))} onDoubleClick={() => onOpen(a.id)} onFocus={(e) => e.target === e.currentTarget && onFocusItem(a.id)}>
                <td className="select-col">
                  <input type="checkbox" className="select-check" checked={selection.has(a.id)} aria-label={`Select ${a.title}`} data-testid="asset-check" tabIndex={-1}
                    onChange={() => {}} onDoubleClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); onCheck(a.id, { shiftKey: e.shiftKey, metaKey: true }); }} />
                </td>
                <td className="thumb-col">{a.thumbnailUrl ? <img key={a.thumbnailUrl} className="mini-thumb" src={a.thumbnailUrl} alt="" loading="lazy"
                  ref={markIfComplete} onLoad={markLoaded} onError={markLoaded} /> : <div className="mini-thumb" />}</td>
                <td style={{ fontWeight: 550 }} title={a.title}>
                  <span className="cell-clip title" style={pad}>
                    {n !== null && <span className="grouped-number" data-testid="grouped-member-number" title={`${l.memberLabel ?? 'Number'} ${n}`}>{n}</span>}
                    {a.restricted && <Lock size={11} className="restricted-mark" aria-label="Restricted" />}{a.title}
                    {a.metadataIncomplete && <span className="incomplete-mark" title="Incomplete metadata: a needed field is empty" role="img" aria-label="Incomplete metadata"><AlertTriangle size={13} /></span>}
                    <RightsMark rights={a.rights} />{a.qc && <span style={{ marginLeft: 6 }}><QcBadge status={a.qc.effectiveStatus} resolved={a.qc.resolved} compact /></span>}<MasterBadge asset={a} compact />
                  </span>
                </td>
                {columns.map((c) => <td key={c.id} className={c.mono ? 'mono' : c.muted ? 'muted' : undefined}><span className="cell-clip">{c.render(a)}</span></td>)}
              </tr>
            );
          })}
        </tbody>
      </table>
      {root && (
        <div ref={rootMore} className="grouped-more" aria-live="polite" data-testid="grouped-root-more">
          {stopped && root.hasNext && (rootSearching || !root.rows.length)
            ? <><span className="faint" data-testid="grouped-stopped">Search stopped.</span>{' '}
              <button className="btn ghost sm" onClick={() => setStoppedFor(null)} data-testid="grouped-search-more">Search more</button></>
            : rootSearching ? <span className="faint" data-testid="grouped-searching">{root.phase === 'ungrouped' ? 'Searching more assets…' : 'Searching more series…'}</span>
              : root.fetchingNext ? 'Loading more…' : root.hasNext ? <button className="btn ghost sm" onClick={() => fetchers.current.get('root')?.()}>Load more</button> : null}
          {anySearching && !stopped && (
            <>{' '}<button className="btn ghost sm" onClick={stopSearching} data-testid="grouped-stop" title="Stop searching (Search more goes on)"><Square size={12} /> Stop</button></>
          )}
        </div>
      )}
    </div>
  );
}

export type { Line as GroupedLine, LevelState as GroupedLevelState };
